import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import Landing from "@/pages/Landing";
import { Login, Register, Forgot, Reset } from "@/pages/auth/AuthPages";
import Dashboard from "@/pages/Dashboard";
import Trash from "@/pages/Trash";
import Leads from "@/pages/Leads";
import Account from "@/pages/Account";
import Editor from "@/pages/Editor";
import PublicPage, { PublicFunnel, DomainPage } from "@/pages/PublicPage";
import Domains from "@/pages/Domains";
import Funnels, { FunnelDetail } from "@/pages/Funnels";
import AcceptInvite from "@/pages/AcceptInvite";

const APP_HOST = new URL(process.env.REACT_APP_BACKEND_URL).host;
const isCustomDomain = () => { const h = window.location.host; return h !== APP_HOST && !h.startsWith("localhost") && !h.includes("127.0.0.1") && !h.endsWith(".emergentagent.com") && !h.endsWith(".emergent.host"); };

const Loading = () => (
  <div className="min-h-screen grid place-items-center bg-[var(--bg)]"><div className="h-8 w-8 rounded-full border-2 border-slate-200 border-t-[var(--blue)] animate-spin" /></div>
);

const Protected = ({ children }) => {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  return children;
};

// Google OAuth callback: exchanges #session_id for a session cookie
const AuthCallback = () => {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const id = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    api.post("/auth/session", { session_id: id })
      .then(({ data }) => { setUser(data); window.history.replaceState(null, "", "/app"); nav("/app", { replace: true, state: { user: data } }); })
      .catch(() => nav("/login?error=google", { replace: true }));
  }, [nav, setUser]);
  return <Loading />;
};

const Router = () => {
  const loc = useLocation();
  if (loc.hash?.includes("session_id=")) return <AuthCallback />;
  if (isCustomDomain()) return <DomainPage />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/f/:slug" element={<PublicFunnel />} />
      <Route path="/f/:slug/:step" element={<PublicFunnel />} />
      <Route path="/app/domains" element={<Protected><Domains /></Protected>} />
      <Route path="/app/funnels" element={<Protected><Funnels /></Protected>} />
      <Route path="/app/funnels/:id" element={<Protected><FunnelDetail /></Protected>} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot" element={<Forgot />} />
      <Route path="/reset/:token" element={<Reset />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/p/:slug" element={<PublicPage />} />
      <Route path="/app" element={<Protected><Dashboard /></Protected>} />
      <Route path="/app/trash" element={<Protected><Trash /></Protected>} />
      <Route path="/app/leads" element={<Protected><Leads /></Protected>} />
      <Route path="/app/account" element={<Protected><Account /></Protected>} />
      <Route path="/editor/:id" element={<Protected><Editor /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Router />
          <Toaster position="bottom-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
